import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Bell, Settings, ClipboardList, Search, X, RefreshCw, Filter,
} from 'lucide-react';
import api from '@/lib/api';

// Phase 15-B follow-up · Seller-facing Notification Dispatch Log Viewer.
// Reads /api/seller/notifications/dispatch-logs (filtered) + /dispatch-logs-summary.
// Real Kakao send + wallet deduction is a Phase 15-C concern. Under the
// current Mock provider `cost_krw` is always 0 — the summary card labels make
// that explicit so a seller does not misread the "예상 사용금액" figure.

const EVENT_LABEL = {
  order_created: '주문 접수',
  comment_order_confirmed: '댓글주문 확정',
  payment_completed: '결제 완료',
  shipping_started: '배송 시작',
  delivery_completed: '배송 완료',
  inquiry_answered: '문의 답변',
};

const STATUS_META = {
  sent:                          { label: '발송완료', tone: 'emerald' },
  failed:                        { label: '발송실패', tone: 'red' },
  skipped_disabled:              { label: '설정 OFF', tone: 'gray' },
  skipped_no_phone:              { label: '전화번호 없음', tone: 'amber' },
  skipped_insufficient_balance:  { label: '충전금 부족', tone: 'orange' },
  queued:                        { label: '발송대기', tone: 'blue' },
};

const TONE = {
  emerald: 'bg-emerald-500 text-white',
  red:     'bg-red-500 text-white',
  gray:    'bg-black/10 dark:bg-white/15 text-gray-500 dark:text-gray-300',
  amber:   'bg-amber-500 text-white',
  orange:  'bg-orange-500 text-white',
  blue:    'bg-blue-500 text-white',
};

const CHANNEL_LABEL = { kakao_alimtalk: '카카오 알림톡', sms: '문자', email: '이메일' };

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'gray' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-black ${TONE[meta.tone]}`} data-testid={`notif-status-badge-${status}`}>
      {meta.label}
    </span>
  );
};

const StatCard = ({ label, value, hint, testId }) => (
  <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 bg-white dark:bg-[#141414]" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">{label}</p>
    <p className="text-lg font-black mt-1">{value ?? '-'}</p>
    {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

// Simple date range presets → yyyy-mm-dd strings
const rangePreset = (kind) => {
  const now = new Date();
  const toStr = (d) => d.toISOString().slice(0, 10);
  if (kind === 'today') return { from: toStr(now), to: toStr(now) };
  if (kind === '7d') { const p = new Date(now); p.setDate(now.getDate() - 6); return { from: toStr(p), to: toStr(now) }; }
  if (kind === '30d') { const p = new Date(now); p.setDate(now.getDate() - 29); return { from: toStr(p), to: toStr(now) }; }
  return { from: '', to: '' };
};

const StatusFilter = ({ value, onChange }) => {
  const opts = [
    { k: '',                 label: '전체'    },
    { k: 'sent',             label: '발송완료' },
    { k: 'failed',           label: '발송실패' },
    { k: 'skipped',          label: '스킵'    },
  ];
  return (
    <div className="flex gap-1 overflow-x-auto -mx-2 px-2" data-testid="notif-logs-status-tabs">
      {opts.map((o) => (
        <button key={o.k || 'all'} onClick={() => onChange(o.k)} data-testid={`notif-logs-status-${o.k || 'all'}`}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${value === o.k ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-black/10 dark:hover:bg-white/20'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
};

const DetailDrawer = ({ logId, onClose }) => {
  const [row, setRow] = useState(null);
  useEffect(() => {
    if (!logId) return;
    let alive = true;
    api.get(`/seller/notifications/dispatch-logs/${logId}`)
      .then((r) => { if (alive) setRow(r.data); })
      .catch(() => toast.error('로그 상세를 불러오지 못했습니다.'));
    return () => { alive = false; };
  }, [logId]);
  if (!logId) return null;
  const Field = ({ label, value, testId, mono }) => (
    <div className="grid grid-cols-3 gap-2 py-1.5" data-testid={testId}>
      <div className="text-[11px] text-gray-500 font-bold">{label}</div>
      <div className={`col-span-2 text-[12px] break-all ${mono ? 'font-mono text-[11px]' : ''}`}>{value ?? '-'}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} data-testid="notif-log-drawer-backdrop">
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-white dark:bg-[#0F0F0F] shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="notif-log-drawer">
        <div className="sticky top-0 z-10 flex items-center justify-between p-3 border-b border-black/10 dark:border-white/10 bg-white/95 dark:bg-[#0F0F0F]/95 backdrop-blur">
          <p className="text-sm font-black inline-flex items-center gap-1.5"><Bell size={14} className="text-[#FF3B30]" /> 알림 발송 상세</p>
          <button onClick={onClose} data-testid="notif-log-drawer-close" className="p-1"><X size={16} /></button>
        </div>
        {!row ? (
          <div className="p-8 text-center text-xs text-gray-500">불러오는 중…</div>
        ) : (
          <div className="p-4 divide-y divide-black/5 dark:divide-white/10">
            <div className="pb-2">
              <div className="flex items-center gap-2 mb-2">
                <StatusBadge status={row.status} />
                <span className="text-[10px] font-black bg-black/10 dark:bg-white/15 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full">
                  {EVENT_LABEL[row.event_type] || row.event_type}
                </span>
              </div>
              <p className="text-sm font-bold">{row.customer_name || '(고객 정보 없음)'}</p>
              <p className="text-[11px] text-gray-500">{fmtDate(row.created_at)}</p>
            </div>
            <div className="py-2">
              <Field label="채널"      value={CHANNEL_LABEL[row.channel] || row.channel} />
              <Field label="템플릿"    value={row.template_code} testId="notif-log-tpl" mono />
              <Field label="수신 전화" value={row.recipient_phone} />
            </div>
            <div className="py-2">
              <Field label="주문번호"  value={row.order_number ? <Link to={`/orders/${row.order_id}`} className="text-[#FF3B30] hover:underline">{row.order_number}</Link> : '-'} />
              <Field label="문의번호"  value={row.inquiry_id ? `${row.inquiry_id} · ${row.inquiry_title || ''}` : '-'} mono />
              <Field label="문의 상태" value={row.inquiry_status || '-'} />
            </div>
            <div className="py-2">
              <Field label="상태 사유"          value={row.skip_reason || row.error || '-'} />
              <Field label="Provider 메시지"    value={row.provider_message_id} mono />
              <Field label="Provider 응답"      value={row.provider_response ? <pre className="text-[10px] whitespace-pre-wrap bg-black/5 dark:bg-white/5 rounded p-2">{JSON.stringify(row.provider_response, null, 2)}</pre> : '-'} />
              <Field label="Idempotency Key"   value={row.idempotency_key} mono />
              <Field label="비용"                value={`${won(row.cost_krw)} (TEST MODE: 실제 차감 없음)`} testId="notif-log-cost" />
              <Field label="지갑 트랜잭션"       value={row.wallet_tx_id} mono />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function SellerNotificationLogs() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [rangeKind, setRangeKind] = useState('30d');
  const [range, setRange] = useState(rangePreset('30d'));
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const loadSummary = () => {
    api.get('/seller/notifications/dispatch-logs-summary').then((r) => setSummary(r.data)).catch(() => {});
  };
  const loadRows = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (statusFilter) params.status = statusFilter;
    if (eventFilter) params.event_type = eventFilter;
    if (range.from) params.from_date = range.from;
    if (range.to) params.to_date = range.to;
    if (debouncedQ) params.q = debouncedQ;
    api.get('/seller/notifications/dispatch-logs', { params })
      .then((r) => setRows(r.data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || '로그를 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  };

  useEffect(loadSummary, []);
  useEffect(loadRows, [statusFilter, eventFilter, range.from, range.to, debouncedQ]);

  const applyRange = (kind) => {
    setRangeKind(kind);
    if (kind !== 'custom') setRange(rangePreset(kind));
  };

  const isMock = summary?.is_test_mode !== false; // default TEST until backend confirms otherwise
  const unit = summary?.unit_price;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto p-4 pb-16" data-testid="seller-notif-logs">
        <Link to="/seller/dashboard" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white pt-2 pb-3" data-testid="notif-logs-back">
          <ArrowLeft size={14} /> 판매자 대시보드
        </Link>

        {/* Sub-nav */}
        <div className="flex items-center gap-1 mb-3 border-b border-black/10 dark:border-white/10 -mx-4 px-4">
          <Link to="/seller/notifications" data-testid="notif-subnav-settings"
            className="inline-flex items-center gap-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold border-b-2 border-transparent text-gray-500 hover:text-black dark:hover:text-white -mb-px">
            <Settings size={13} /> 고객 알림톡 설정
          </Link>
          <Link to="/seller/notifications/logs" data-testid="notif-subnav-logs"
            className="inline-flex items-center gap-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold border-b-2 border-[#FF3B30] text-[#FF3B30] -mb-px">
            <ClipboardList size={13} /> 알림 발송내역
          </Link>
        </div>

        <div className="flex items-center gap-2 mb-1">
          <Bell size={16} className="text-[#FF3B30]" />
          <h1 className="text-xl font-black">알림 발송내역</h1>
          {isMock && (
            <span className="text-[10px] font-black bg-orange-500 text-white px-1.5 py-0.5 rounded-full" data-testid="notif-logs-test-badge">TEST MODE</span>
          )}
        </div>
        {isMock && (
          <p className="text-[11px] text-gray-500 mb-4 leading-relaxed" data-testid="notif-logs-test-notice">
            현재는 <b>Mock 발송 모드</b>입니다. 실제 카카오톡이 발송되지 않고 시스템 로그에만 기록됩니다.
            판매자 충전금은 차감되지 않으며, 아래 <b>예상 사용금액</b>은 정식 오픈 후 실제 사용 금액을 표시합니다.
          </p>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <StatCard label="오늘 발송"      value={summary?.today_sent}    hint={`오늘 총 ${summary?.today_total ?? 0}건`} testId="notif-summary-today" />
          <StatCard label="이번 달 발송"    value={summary?.month_sent}    hint={`총 ${summary?.month_total ?? 0}건`}    testId="notif-summary-month" />
          <StatCard label="발송 실패"      value={summary?.month_failed}  hint="이번 달"                             testId="notif-summary-failed" />
          <StatCard label="스킵"          value={summary?.month_skipped} hint="이번 달"                             testId="notif-summary-skipped" />
          <StatCard label="예상 사용금액"   value={isMock ? '0원 (TEST)' : won(summary?.month_estimated_cost)}
                                            hint={unit != null ? `단가 ${won(unit)}/건` : ' '}
                                            testId="notif-summary-cost" />
        </div>

        {/* Filters */}
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />

        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_auto_auto] items-center">
          {/* Search */}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="주문번호 · 고객명 · 전화번호 · 문의번호"
              className="w-full pl-7 pr-8 py-2 text-xs sm:text-sm border rounded-full bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
              data-testid="notif-logs-search" />
            {q && (
              <button onClick={() => setQ('')} className="absolute right-2 top-2 text-gray-400" data-testid="notif-logs-search-clear"><X size={12} /></button>
            )}
          </div>
          {/* Event */}
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
            className="px-2 py-2 text-xs border rounded-full bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
            data-testid="notif-logs-event-filter">
            <option value="">전체 유형</option>
            {Object.entries(EVENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {/* Range */}
          <select value={rangeKind} onChange={(e) => applyRange(e.target.value)}
            className="px-2 py-2 text-xs border rounded-full bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
            data-testid="notif-logs-range-preset">
            <option value="today">오늘</option>
            <option value="7d">7일</option>
            <option value="30d">30일</option>
            <option value="custom">직접 선택</option>
          </select>
          <button onClick={() => { loadRows(); loadSummary(); }} className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-black text-white text-xs font-bold" data-testid="notif-logs-refresh">
            <RefreshCw size={12} /> 새로고침
          </button>
        </div>

        {rangeKind === 'custom' && (
          <div className="flex items-center gap-2 mt-2 text-xs" data-testid="notif-logs-custom-range">
            <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="px-2 py-1 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
              data-testid="notif-logs-from" />
            <span className="text-gray-400">~</span>
            <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="px-2 py-1 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
              data-testid="notif-logs-to" />
          </div>
        )}

        {/* List */}
        <div className="mt-4">
          {loading ? (
            <p className="text-center text-xs text-gray-500 py-10">불러오는 중…</p>
          ) : rows.length === 0 ? (
            <div className="text-center py-14 rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="notif-logs-empty">
              <Filter size={16} className="mx-auto text-gray-300" />
              <p className="text-xs text-gray-500 mt-2">조건에 맞는 발송 내역이 없습니다.</p>
            </div>
          ) : (
            <>
              {/* PC table */}
              <div className="hidden md:block overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10" data-testid="notif-logs-table">
                <table className="w-full text-xs">
                  <thead className="bg-black/5 dark:bg-white/5 text-gray-500">
                    <tr className="text-left">
                      <th className="p-2 font-bold">발송일시</th>
                      <th className="p-2 font-bold">고객</th>
                      <th className="p-2 font-bold">전화번호</th>
                      <th className="p-2 font-bold">이벤트</th>
                      <th className="p-2 font-bold">주문/문의</th>
                      <th className="p-2 font-bold">상태</th>
                      <th className="p-2 font-bold text-right">비용</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/10">
                    {rows.map((r) => (
                      <tr key={r.log_id} onClick={() => setOpenId(r.log_id)}
                          className="cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                          data-testid={`notif-log-row-${r.log_id}`}>
                        <td className="p-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="p-2">{r.customer_name || <span className="text-gray-400">-</span>}</td>
                        <td className="p-2 whitespace-nowrap">{r.recipient_phone || <span className="text-gray-400">-</span>}</td>
                        <td className="p-2 whitespace-nowrap">{EVENT_LABEL[r.event_type] || r.event_type}</td>
                        <td className="p-2 whitespace-nowrap">
                          {r.order_number && <span className="font-mono text-[10px]">{r.order_number}</span>}
                          {r.inquiry_id && !r.order_number && <span className="font-mono text-[10px]">{r.inquiry_id}</span>}
                          {!r.order_number && !r.inquiry_id && <span className="text-gray-400">-</span>}
                        </td>
                        <td className="p-2"><StatusBadge status={r.status} /> {r.skip_reason && <span className="text-[10px] text-gray-400 ml-1">{r.skip_reason}</span>}</td>
                        <td className="p-2 text-right whitespace-nowrap">{isMock ? '0원' : won(r.cost_krw)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="md:hidden grid gap-2" data-testid="notif-logs-cards">
                {rows.map((r) => (
                  <button key={r.log_id} onClick={() => setOpenId(r.log_id)}
                          className="w-full text-left rounded-2xl border border-black/10 dark:border-white/10 p-3 bg-white dark:bg-[#141414]"
                          data-testid={`notif-log-card-${r.log_id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <StatusBadge status={r.status} />
                      <p className="text-[10px] text-gray-400">{fmtDate(r.created_at)}</p>
                    </div>
                    <p className="text-sm font-bold">{EVENT_LABEL[r.event_type] || r.event_type} · {r.customer_name || '고객 정보 없음'}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {r.recipient_phone || '전화번호 없음'} · {r.order_number || r.inquiry_id || '연결 없음'}
                    </p>
                    {r.skip_reason && <p className="text-[10px] text-amber-500 mt-0.5">{r.skip_reason}</p>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <DetailDrawer logId={openId} onClose={() => setOpenId(null)} />
      </div>
    </div>
  );
}
